import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Clock, Ban, ArrowRight, MapPin, Mic, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import BusinessCard from "@/components/BusinessCard";
import FeaturedStrip from "@/components/FeaturedStrip";
import LaunchBanner from "@/components/LaunchBanner";
import { KnowMoga } from "@/pages/MogaPage";
import { MogaBanner } from "@/components/MogaBanner";
import { TownSelect, AdBanner, DealsStrip } from "@/components/HomeExtras";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/lib/i18n";
import { useCatalog, useApi, useTown, useListSub } from "@/lib/data";
import { CategoryIcon } from "@/lib/icons";

const TrustStrip = () => {
  const { t, lp } = useT();
  const items = [[ShieldCheck, "trust_1"], [Clock, "trust_2"], [Ban, "trust_3"]];
  return (
    <Link to={lp("/how-it-works")} className="mx-5 mt-6 block rounded-2xl border border-amber-200 bg-amber-50 p-4 lg:mx-8 lg:p-6" data-testid="trust-strip">
      <div className="grid grid-cols-3 gap-2">
        {items.map(([Icon, k]) => (
          <div key={k} className="flex flex-col items-center gap-1.5 text-center">
            <Icon className="h-5 w-5 text-saffron" />
            <span className="text-[11px] font-semibold leading-tight text-amber-950">{t(k)}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 flex items-center justify-center gap-1 text-xs font-bold text-saffron">{t("nav_how")} <ArrowRight className="h-3.5 w-3.5" /></p>
    </Link>
  );
};

const Hero = ({ town, setTown, locality, setLocality }) => {
  const { t, tn, lp, lang } = useT();
  const { localities: allLocs } = useCatalog();
  const localities = allLocs.filter((l) => !town || l.town_id === town);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const navigate = useNavigate();
  const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
  const listen = () => {
    const r = new Speech();
    r.lang = { pa: "pa-IN", hi: "hi-IN" }[lang] || "en-IN";
    r.onresult = (e) => setQ(e.results[0][0].transcript);
    r.onend = () => setListening(false);
    setListening(true);
    r.start();
  };
  const go = async (e) => {
    e.preventDefault();
    const text = q.trim();
    const p = new URLSearchParams();
    if (text.split(/\s+/).length >= 2) {
      setBusy(true);
      const r = await api.post("/ask", { q: text }).then((x) => x.data).catch(() => ({}));
      setBusy(false);
      if (r.ok && (r.category || r.locality || r.q || r.open_now)) {
        [["category", r.category], ["locality", r.locality], ["q", r.q], ["open", r.open_now ? "1" : ""]].forEach(([k, v]) => v && p.set(k, v));
        if (!r.locality && town) p.set("town", town);
        p.set("asked", text);
        return navigate(`${lp("/browse")}?${p}`);
      }
    }
    if (text) p.set("q", text);
    if (locality) p.set("locality", locality);
    else if (town) p.set("town", town);
    navigate(`${lp("/browse")}?${p}`);
  };
  return (
    <section className="hero-pattern relative overflow-hidden px-5 pb-7 pt-8 text-white lg:mx-8 lg:mt-6 lg:rounded-3xl lg:px-12 lg:py-16" data-testid="home-hero">
      <MogaBanner />
      <div className="relative z-10 pb-4">
      <p className={`text-xs font-bold text-amber-100 ${lang === "en" ? "uppercase tracking-[0.18em]" : ""}`}>{t("kicker")}</p>
      <h1 className="mt-2 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">{t("hero_title")}</h1>
      <p className="mt-3 max-w-xs text-sm text-amber-50/90 lg:max-w-xl lg:text-base">{t("hero_sub")}</p>
      <form onSubmit={go} className="mt-6 flex max-w-2xl items-center gap-2 rounded-2xl bg-white p-1.5 shadow-lg" data-testid="home-search-form">
        <Search className="ml-2 h-5 w-5 flex-shrink-0 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")}
          className="min-w-0 flex-1 bg-transparent py-2.5 text-base text-ink outline-none placeholder:text-slate-400" data-testid="home-search-input" />
        {Speech && <button type="button" onClick={listen} aria-label={t("voice_search")} className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${listening ? "animate-pulse bg-red-100 text-red-600" : "text-slate-500 hover:bg-slate-100"}`} data-testid="home-voice-btn"><Mic className="h-5 w-5" /></button>}
        <button disabled={busy} className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white" data-testid="home-search-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("search_btn")}</button>
      </form>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-50/90" data-testid="home-ask-hint"><Sparkles className="h-3.5 w-3.5" />{t("ask_hint")}</p>
      <div className="mt-3 flex max-w-2xl items-center gap-2">
        <TownSelect value={town} onChange={setTown} className="h-10 w-auto min-w-[130px] border-white/30 bg-white/15 text-white" testid="home-town-select" />
        <MapPin className="h-4 w-4 text-amber-100" />
        <Select value={locality || "all"} onValueChange={(v) => setLocality(v === "all" ? "" : v)}>
          <SelectTrigger className="h-10 flex-1 border-white/30 bg-white/15 text-white" data-testid="home-locality-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="home-locality-all">{t("all_localities")}</SelectItem>
            {localities.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`home-locality-${l.slug}`}>{tn(l.name)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      </div>
    </section>
  );
};

const CategoryGrid = ({ locality, town }) => {
  const { t, tn, lp } = useT();
  const { categories } = useCatalog();
  return (
    <section className="px-5 pt-7 lg:px-8 lg:pt-10">
      <h2 className="text-base font-bold text-ink md:text-lg">{t("popular_categories")}</h2>
      <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8 lg:gap-3" data-testid="category-grid">
        {categories.map((c, i) => (
          <Link key={c.id} to={`${lp("/browse")}?category=${c.id}${locality ? `&locality=${locality}` : town ? `&town=${town}` : ""}`}
            style={{ animationDelay: `${Math.min(i, 24) * 25}ms` }} data-testid={`category-tile-${c.slug}`}
            className="animate-rise flex flex-col items-center gap-1.5 rounded-2xl border border-line bg-white px-1 py-3 text-center transition-transform active:scale-95">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-saffron-50 text-saffron"><CategoryIcon name={c.icon} className="h-6 w-6" /></span>
            <span className="line-clamp-2 text-[11px] font-semibold leading-tight text-ink">{tn(c.name)}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const OwnerCta = () => {
  const { t, lp } = useT();
  return (
    <Link to={lp("/owner")} className="mx-5 mt-8 flex items-center justify-between rounded-2xl bg-ink p-5 text-white lg:mx-8 lg:p-8" data-testid="home-owner-cta">
      <div><p className="font-display text-lg font-bold">{t("list_free")}</p><p className="text-xs text-slate-300">{t(useListSub())}</p></div>
      <ArrowRight className="h-5 w-5 text-amber-300" />
    </Link>
  );
};

export default function Home() {
  const [locality, setLocality] = useState("");
  const [town, setTownRaw] = useTown();
  const setTown = (v) => { setTownRaw(v); setLocality(""); };
  const { data = [] } = useApi(["businesses", locality, town], `/businesses?locality=${locality}&town=${town}&limit=6`);
  return (
    <Layout seo={{ jsonLd: { "@context": "https://schema.org", "@type": "WebSite", name: "Moga 360", url: window.location.origin } }}>
      <LaunchBanner />
      <Hero town={town} setTown={setTown} locality={locality} setLocality={setLocality} />
      <AdBanner town={town} />
      <FeaturedStrip town={town} />
      <DealsStrip town={town} />
      <CategoryGrid locality={locality} town={town} />
      <KnowMoga />
      <TrustStrip />
      {data.length > 0 && (
        <section className="grid gap-3 px-5 pt-8 md:grid-cols-2 lg:grid-cols-3 lg:px-8" data-testid="home-recent-list">
          {data.slice(0, 6).map((b) => <BusinessCard key={b.id} b={b} />)}
        </section>
      )}
      <OwnerCta />
    </Layout>
  );
}
